import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { ArrowLeft, Calendar, CheckCircle, Clock, TrendingUp, Users, FlaskConical, ClipboardList, AlertCircle, Target, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, LineChart, Line, CartesianGrid } from 'recharts';
import { format } from 'date-fns';
import ProblemDetailModal from '../components/ProblemDetailModal';
import ProblemFormModal from '../components/ProblemFormModal';
import { updateProblem, deleteProblem, saveSolutions, saveSubtasks } from '../utils/supabaseHelpers';

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [experiments, setExperiments] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [subtasks, setSubtasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showEditFormModal, setShowEditFormModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [selectedDateSubtasks, setSelectedDateSubtasks] = useState([]);
  const [showDateSubtasksModal, setShowDateSubtasksModal] = useState(false);
  const [timelineMonthOffset, setTimelineMonthOffset] = useState(0);

  // Handle opening task in modal
  const handleOpenTask = async (problemId) => {
    try {
      const { data, error } = await supabase
        .from('problems')
        .select('*, solutions (*), subtasks (*, assignee:users(name, is_active, inactive_user_name)), assignee:users!assigned_to(id, name, email, is_active, inactive_user_name)')
        .eq('id', problemId)
        .single();

      if (error) throw error;
      setSelectedTask(data);
      setShowTaskModal(true);
    } catch (error) {
      console.error('Error loading task:', error);
      toast.error('Failed to load task details');
    }
  };

  const handleCloseTaskModal = () => {
    setShowTaskModal(false);
    setSelectedTask(null);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setShowEditFormModal(true);
    setShowTaskModal(false);
  };

  const handleUpdateTask = async (updatedTask) => {
    try {
      const updateData = {
        status: updatedTask.status
      };

      if (updatedTask.status === 'Done' && updatedTask.done_date) {
        updateData.done_date = updatedTask.done_date;
      } else if (updatedTask.status !== 'Done') {
        updateData.done_date = null;
      }

      await updateProblem(updatedTask.id, updateData);
      await loadProjectData(false);
      toast.success('Task updated successfully');
      
      // Refresh the selected task data
      if (selectedTask?.id === updatedTask.id) {
        handleOpenTask(updatedTask.id);
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleUpdateTaskFull = async (taskData) => {
    try {
      console.log('=== PROJECT DETAIL UPDATE STARTED ===');
      console.log('🔄 ProjectDetail: Starting task update for ID:', taskData.id);

      // Step 1: Update the task record
      const updateData = {
        title: taskData.title,
        description: taskData.description,
        domain: taskData.domain,
        priority: taskData.priority,
        status: taskData.status,
        due_date: taskData.dueDate || null,
        notes: taskData.notes || ''
      };

      // Include done_date if status is 'Done'
      if (taskData.status === 'Done' && taskData.done_date) {
        updateData.done_date = taskData.done_date;
      } else if (taskData.status !== 'Done') {
        updateData.done_date = null;
      }

      await updateProblem(taskData.id, updateData);
      console.log('✅ ProjectDetail: Task updated successfully');

      // Step 2: Save solutions
      await saveSolutions(taskData.id, taskData.solutions || []);
      console.log('✅ ProjectDetail: Solutions saved');

      // Step 3: Save subtasks
      await saveSubtasks(taskData.id, taskData.subtasks || []);
      console.log('✅ ProjectDetail: Subtasks saved');

      // Step 4: Reload project data
      await loadProjectData(false);

      toast.success('Task updated successfully!');
      setShowEditFormModal(false);
      setEditingTask(null);

      console.log('=== PROJECT DETAIL UPDATE COMPLETED ===');
    } catch (error) {
      console.error('=== PROJECT DETAIL UPDATE ERROR ===');
      console.error('❌ ProjectDetail: Failed to update task:', error);
      toast.error('Failed to update task: ' + error.message);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      await deleteProblem(taskId);
      await loadProjectData(false);
      toast.success('Task deleted successfully');
      handleCloseTaskModal();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  // Load project data function
  const loadProjectData = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }

      // Fetch project details
      const { data: projectData, error: projectError } = await supabase
        .from('experiments')
        .select('*')
        .eq('id', projectId)
        .eq('type', 'project')
        .single();

      if (projectError) throw projectError;
      setProject(projectData);

      // Fetch experiments linked to this project
      const { data: experimentsData, error: experimentsError } = await supabase
        .from('experiments')
        .select('*')
        .eq('project_id', projectId)
        .eq('type', 'experiment')
        .order('created_at', { ascending: false });

      if (experimentsError) throw experimentsError;
      setExperiments(experimentsData || []);

      // Fetch tasks linked to this project using project_id
      let { data: tasksData, error: tasksError } = await supabase
        .from('problems')
        .select(`
          *,
          assignee:users!assigned_to(id, name, email, is_active, inactive_user_name)
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (tasksError) {
        const missingProjectColumn = `${tasksError.message || ''}`.toLowerCase().includes('project_id');

        if (missingProjectColumn) {
          console.warn('project_id column not available on problems table. Falling back to empty project tasks list.');
          tasksData = [];
          tasksError = null;
        }
      }

      if (tasksError) throw tasksError;
      setTasks(tasksData || []);

      // Fetch subtasks for all tasks in this project
      const taskIds = (tasksData || []).map(t => t.id);
      let subtasksData = [];
      if (taskIds.length > 0) {
        const { data: subtasksResult, error: subtasksError } = await supabase
          .from('subtasks')
          .select(`
            *,
            problem:problems!problem_id(id, title),
            assignee:users!assigned_to(id, name, email)
          `)
          .in('problem_id', taskIds)
          .order('created_at', { ascending: false });
        
        if (subtasksError) throw subtasksError;
        subtasksData = subtasksResult || [];
      }
      setSubtasks(subtasksData);

      // Fetch users
      const { data: usersData } = await supabase
        .from('users')
        .select('*');
      setUsers(usersData || []);

    } catch (error) {
      console.error('Error loading project data:', error);
      toast.error('Failed to load project details');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    // Initial data load
    loadProjectData(true);

    // Set up real-time subscriptions for instant updates across all users
    const channelName = `project-detail-${projectId}`;
    
    const channel = supabase
      .channel(channelName, {
        config: {
          broadcast: { self: true }
        }
      })
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'experiments'
        },
        (payload) => {
          console.log('[REALTIME] Experiments changed:', payload.eventType);
          // Reload data without showing loading spinner
          setTimeout(() => loadProjectData(false), 100);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'problems'
        },
        (payload) => {
          console.log('[REALTIME] Problems changed:', payload.eventType);
          // Reload data without showing loading spinner
          setTimeout(() => loadProjectData(false), 100);
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subtasks'
        },
        (payload) => {
          console.log('[REALTIME] Subtasks changed:', payload.eventType);
          // Reload data without showing loading spinner
          setTimeout(() => loadProjectData(false), 100);
        }
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          console.log('[REALTIME] Successfully subscribed to project updates');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('[REALTIME] Channel error:', err);
        } else if (status === 'TIMED_OUT') {
          console.error('[REALTIME] Subscription timed out');
        } else if (status === 'CLOSED') {
          console.log('[REALTIME] Channel closed');
        } else {
          console.log('[REALTIME] Status:', status);
        }
      });

    // Cleanup subscription on unmount
    return () => {
      console.log('[REALTIME] Cleaning up subscription');
      channel.unsubscribe();
      supabase.removeChannel(channel);
    };
  }, [projectId]);

  // Calculate metrics
  const metrics = useMemo(() => {
    // Experiments metrics
    const totalExperiments = experiments.length;
    const successfulExperiments = experiments.filter(e => e.status === 'Success').length;
    const failedExperiments = experiments.filter(e => e.status === 'Failure').length;
    const inProgressExperiments = experiments.filter(e => e.status === 'In Progress').length;
    const successRate = totalExperiments > 0 ? Math.round((successfulExperiments / totalExperiments) * 100) : 0;

    // Tasks metrics
    const totalTasks = tasks.length;
    const completedTasks = tasks.filter(t => t.status === 'Done').length;
    const inProgressTasks = tasks.filter(t => t.status === 'In Progress').length;

    // Subtasks metrics
    const totalSubtasks = subtasks.length;
    const completedSubtasks = subtasks.filter(st => st.is_completed).length;
    const incompleteSubtasks = subtasks.filter(st => !st.is_completed).length;
    
    // Overall project completion based only on subtasks
    const overallCompletion = totalSubtasks > 0 
      ? Math.round((completedSubtasks / totalSubtasks) * 100) 
      : 0;

    return {
      totalExperiments,
      successfulExperiments,
      failedExperiments,
      inProgressExperiments,
      successRate,
      totalTasks,
      completedTasks,
      inProgressTasks,
      totalSubtasks,
      completedSubtasks,
      incompleteSubtasks,
      overallCompletion
    };
  }, [experiments, tasks, subtasks]);

  // Experiment outcomes data for pie chart
  const experimentOutcomesData = [
    { name: 'Success', value: metrics.successfulExperiments, fill: '#ffffff' }, // White
    { name: 'Failure', value: metrics.failedExperiments, fill: '#4b5563' }, // Gray-600
    { name: 'In Progress', value: metrics.inProgressExperiments, fill: '#9ca3af' }, // Gray-400
  ].filter(item => item.value > 0);

  // Subtask status data for bar chart (based on subtasks, not tasks)
  const subtaskStatusData = [
    { status: 'Completed', count: subtasks.filter(st => st.is_completed).length, fill: '#ffffff' },
    { status: 'In Progress', count: subtasks.filter(st => !st.is_completed).length, fill: '#9ca3af' },
  ];

  // Activity timeline (combine tasks and subtasks by date)
  const activityData = useMemo(() => {
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      const tasksCount = tasks.filter(t => 
        t.created_at?.split('T')[0] === dateStr || t.updated_at?.split('T')[0] === dateStr
      ).length;
      
      const subtasksCount = subtasks.filter(st => 
        st.created_at?.split('T')[0] === dateStr || st.updated_at?.split('T')[0] === dateStr
      ).length;

      last7Days.push({
        date: format(date, 'MMM dd'),
        tasks: tasksCount,
        subtasks: subtasksCount
      });
    }
    return last7Days;
  }, [tasks, subtasks]);

  // Team workload
  const teamWorkload = useMemo(() => {
    const workloadMap = {};
    tasks.forEach(task => {
      if (task.assigned_to && task.status !== 'Done') {
        const isInactive = task.assignee?.is_active === false;
        const userName = isInactive && task.assignee?.inactive_user_name 
          ? task.assignee.inactive_user_name 
          : (task.assignee?.name || 'Unknown');
        workloadMap[userName] = (workloadMap[userName] || 0) + 1;
      }
    });
    return Object.entries(workloadMap).map(([name, count]) => ({ name, count }));
  }, [tasks]);

  // Subtasks timeline data (group by due date)
  // Include tasks without subtasks and subtasks from tasks with subtasks
  const subtasksTimelineData = useMemo(() => {
    const dateMap = {};
    
    // Get task IDs that have subtasks
    const taskIdsWithSubtasks = new Set(subtasks.map(st => st.problem_id));
    
    // Add subtasks from tasks that have subtasks
    subtasks
      .filter(st => st.due_date)
      .forEach(st => {
        const dateKey = st.due_date;
        if (!dateMap[dateKey]) {
          dateMap[dateKey] = {
            date: dateKey,
            dateFormatted: format(new Date(dateKey), 'MMM dd'),
            count: 0,
            items: []
          };
        }
        dateMap[dateKey].count++;
        dateMap[dateKey].items.push({
          type: 'subtask',
          id: st.id,
          title: st.title,
          due_date: st.due_date,
          is_completed: st.is_completed,
          problem_id: st.problem_id,
          problem: st.problem,
          assignee: st.assignee
        });
      });
    
    // Add tasks that don't have subtasks but have due_date
    tasks
      .filter(task => task.due_date && !taskIdsWithSubtasks.has(task.id))
      .forEach(task => {
        const dateKey = task.due_date;
        if (!dateMap[dateKey]) {
          dateMap[dateKey] = {
            date: dateKey,
            dateFormatted: format(new Date(dateKey), 'MMM dd'),
            count: 0,
            items: []
          };
        }
        dateMap[dateKey].count++;
        dateMap[dateKey].items.push({
          type: 'task',
          id: task.id,
          title: task.title,
          due_date: task.due_date,
          is_completed: task.status === 'Done',
          problem_id: task.id,
          assignee: task.assignee
        });
      });

    return Object.values(dateMap).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [subtasks, tasks]);

  // Get current month's data based on offset
  const currentMonthData = useMemo(() => {
    const today = new Date();
    
    // Calculate the target month based on offset
    const targetMonth = new Date(today.getFullYear(), today.getMonth() + timelineMonthOffset, 1);
    
    // Get first and last day of the target month
    const monthStart = new Date(targetMonth.getFullYear(), targetMonth.getMonth(), 1);
    monthStart.setHours(0, 0, 0, 0);
    
    const monthEnd = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0);
    monthEnd.setHours(23, 59, 59, 999);
    
    // Get number of days in the month
    const daysInMonth = monthEnd.getDate();

    // Generate all days for the month
    const monthDays = [];
    for (let i = 0; i < daysInMonth; i++) {
      const day = new Date(monthStart);
      day.setDate(monthStart.getDate() + i);
      const dateKey = format(day, 'yyyy-MM-dd');
      
      const existingData = subtasksTimelineData.find(d => d.date === dateKey);
      monthDays.push(existingData || {
        date: dateKey,
        dateFormatted: format(day, 'MMM dd'),
        count: 0,
        subtasks: []
      });
    }

    return {
      data: monthDays,
      monthName: format(monthStart, 'MMMM yyyy'),
      year: format(monthStart, 'yyyy')
    };
  }, [subtasksTimelineData, timelineMonthOffset]);

  const handleTimelineBarClick = (data) => {
    if (data && data.items && data.items.length > 0) {
      setSelectedDateSubtasks(data.items);
      setShowDateSubtasksModal(true);
    }
  };

  const goToPreviousMonth = () => {
    setTimelineMonthOffset(prev => prev - 1);
  };

  const goToNextMonth = () => {
    setTimelineMonthOffset(prev => prev + 1);
  };

  const goToCurrentMonth = () => {
    setTimelineMonthOffset(0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="loading-dots mb-4">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p className="text-gray-400">Loading project details...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle size={64} className="text-red-400 mb-4" />
        <p className="text-gray-400 text-xl mb-4">Project not found</p>
        <button onClick={() => navigate('/experiment-tracking')} className="btn-primary">
          Back to Projects
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/experiment-tracking')}
          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-white mb-2">{project.title}</h1>
          <p className="text-gray-400">{project.description || 'No description provided'}</p>
        </div>
        <button
          onClick={() => loadProjectData(false)}
          disabled={isRefreshing}
          className="px-4 py-2 rounded-lg bg-white hover:bg-gray-200 disabled:bg-gray-800 disabled:opacity-50 transition-all text-black text-sm font-bold flex items-center gap-2 shadow-[0_0_15px_rgba(255,255,255,0.1)]"
          title="Refresh data"
        >
          {isRefreshing ? (
            <>
              <Clock size={16} className="animate-spin" />
              Refreshing...
            </>
          ) : (
            <>
              <TrendingUp size={16} />
              Refresh
            </>
          )}
        </button>
        <div className={`px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest ${
          project.status === 'Completed' ? 'border-2 border-white text-white bg-white/10' :
          project.status === 'In Progress' ? 'border border-gray-400 text-gray-400 bg-white/5' :
          'border border-gray-600 text-gray-500 bg-black'
        }`}>
          {project.status || 'Active'}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6 mb-8">
        <div className="glass-card p-3 lg:p-6 relative overflow-hidden group hover:scale-105 transition-transform">
          <div className="absolute top-0 right-0 p-2 lg:p-4 opacity-10">
            <Target size={48} className="lg:w-16 lg:h-16" />
          </div>
          <div className="relative z-10">
            <div className="text-gray-400 text-xs lg:text-sm mb-1 lg:mb-2">Overall Progress</div>
            <div className="text-2xl lg:text-4xl font-bold text-white mb-1 lg:mb-2">{metrics.overallCompletion}%</div>
            <div className="text-[10px] lg:text-xs text-gray-400 mb-1 lg:mb-2">
              {metrics.completedSubtasks}/{metrics.totalSubtasks} subtasks completed
            </div>
            <div className="w-full bg-white/10 rounded-full h-1.5 lg:h-2">
              <div 
                className="bg-white h-1.5 lg:h-2 rounded-full transition-all duration-500 shadow-[0_0_10px_rgba(255,255,255,0.3)]"
                style={{ width: `${metrics.overallCompletion}%` }}
              />
            </div>
          </div>
        </div>

        <div className="glass-card p-3 lg:p-6 relative overflow-hidden group hover:scale-105 transition-transform">
          <div className="absolute top-0 right-0 p-2 lg:p-4 opacity-10">
            <FlaskConical size={48} className="lg:w-16 lg:h-16" />
          </div>
          <div className="relative z-10">
            <div className="text-gray-400 text-xs lg:text-sm mb-1 lg:mb-2">Experiments</div>
            <div className="text-2xl lg:text-4xl font-bold text-white mb-1 lg:mb-2">{metrics.totalExperiments}</div>
            <div className="text-[10px] lg:text-xs text-gray-500">{metrics.successRate}% success rate</div>
          </div>
        </div>

        <div className="glass-card p-3 lg:p-6 relative overflow-hidden group hover:scale-105 transition-transform">
          <div className="absolute top-0 right-0 p-2 lg:p-4 opacity-10">
            <ClipboardList size={48} className="lg:w-16 lg:h-16" />
          </div>
          <div className="relative z-10">
            <div className="text-gray-400 text-xs lg:text-sm mb-1 lg:mb-2">Tasks</div>
            <div className="text-2xl lg:text-4xl font-bold text-white mb-1 lg:mb-2">{metrics.totalTasks}</div>
            <div className="text-[10px] lg:text-xs text-gray-500">{metrics.completedTasks} completed</div>
          </div>
        </div>

        <div className="glass-card p-3 lg:p-6 relative overflow-hidden group hover:scale-105 transition-transform">
          <div className="absolute top-0 right-0 p-2 lg:p-4 opacity-10">
            <Zap size={48} className="lg:w-16 lg:h-16" />
          </div>
          <div className="relative z-10">
            <div className="text-gray-400 text-xs lg:text-sm mb-1 lg:mb-2">Active Subtasks</div>
            <div className="text-2xl lg:text-4xl font-bold text-white mb-1 lg:mb-2">
              {metrics.incompleteSubtasks}
            </div>
            <div className="text-[10px] lg:text-xs text-gray-500">In progress</div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Experiment Outcomes */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold mb-4 text-white">Experiment Outcomes</h3>
          <div className="h-[250px]">
            {experimentOutcomesData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={experimentOutcomesData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {experimentOutcomesData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No experiments yet
              </div>
            )}
          </div>
        </div>

        {/* Subtask Status */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold mb-4 text-white">Subtask Status</h3>
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={subtaskStatusData}>
                <XAxis dataKey="status" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} />
                <Tooltip />
                <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                  {subtaskStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Activity Timeline */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold mb-4 text-white">7-Day Activity</h3>
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={activityData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={10} />
                <YAxis stroke="#6b7280" fontSize={12} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                  labelStyle={{ color: '#9ca3af' }}
                />
                <Legend wrapperStyle={{ paddingTop: '10px' }} />
                <Line 
                  type="monotone" 
                  dataKey="tasks" 
                  stroke="#6366f1" 
                  strokeWidth={2} 
                  name="Tasks"
                  dot={{ fill: '#6366f1', r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="subtasks" 
                  stroke="#14b8a6" 
                  strokeWidth={2} 
                  name="Subtasks"
                  dot={{ fill: '#14b8a6', r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Subtasks Timeline */}
      <div className="glass-card p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Subtasks Timeline</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={goToPreviousMonth}
              className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors text-gray-300 text-sm flex items-center gap-1"
            >
              <ArrowLeft size={16} />
              Previous
            </button>
            {timelineMonthOffset !== 0 && (
              <button
                onClick={goToCurrentMonth}
                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors text-white text-sm"
              >
                Current Month
              </button>
            )}
            <button
              onClick={goToNextMonth}
              className="px-3 py-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors text-gray-300 text-sm flex items-center gap-1"
            >
              Next
              <ArrowLeft size={16} className="rotate-180" />
            </button>
          </div>
        </div>
        <div className="text-sm text-gray-400 mb-4">
          {currentMonthData.monthName}
        </div>
        <div className="h-[400px]">
          {subtasksTimelineData.length > 0 || currentMonthData.data.some(d => d.count > 0) ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={currentMonthData.data}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                <XAxis 
                  dataKey="dateFormatted" 
                  stroke="#6b7280" 
                  fontSize={12}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  stroke="#6b7280" 
                  fontSize={12}
                  allowDecimals={false}
                  label={{ value: 'Number of Subtasks', angle: -90, position: 'insideLeft', style: { fill: '#6b7280' } }}
                />
                <Tooltip 
                  content={({ payload }) => {
                    if (!payload || payload.length === 0) return null;
                    const data = payload[0].payload;
                    if (data.count === 0) {
                      return (
                        <div className="bg-[#1f2937] border border-white/10 rounded-lg p-3 shadow-xl">
                          <p className="text-white font-medium mb-1">{format(new Date(data.date), 'MMMM dd, yyyy')}</p>
                          <p className="text-gray-400 text-sm">No tasks or subtasks due</p>
                        </div>
                      );
                    }
                    const subtaskCount = data.items?.filter(item => item.type === 'subtask').length || 0;
                    const taskCount = data.items?.filter(item => item.type === 'task').length || 0;
                    return (
                      <div className="bg-[#1f2937] border border-white/10 rounded-lg p-3 shadow-xl">
                        <p className="text-white font-medium mb-1">{format(new Date(data.date), 'MMMM dd, yyyy')}</p>
                        <p className="text-gray-400 text-sm">
                          {taskCount > 0 && `${taskCount} task${taskCount !== 1 ? 's' : ''}`}
                          {taskCount > 0 && subtaskCount > 0 && ', '}
                          {subtaskCount > 0 && `${subtaskCount} subtask${subtaskCount !== 1 ? 's' : ''}`}
                          {' due'}
                        </p>
                        {data.count > 0 && <p className="text-indigo-400 text-xs mt-1">Click to view details</p>}
                      </div>
                    );
                  }}
                />
                <Bar 
                  dataKey="count" 
                  radius={[8, 8, 0, 0]}
                  onClick={handleTimelineBarClick}
                  cursor="pointer"
                >
                  {currentMonthData.data.map((entry, index) => {
                    const isToday = format(new Date(entry.date), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
                    // Check if date is in the past (before today, not including today)
                    const entryDate = new Date(entry.date);
                    entryDate.setHours(23, 59, 59, 999);
                    const today = new Date();
                    today.setHours(23, 59, 59, 999);
                    const isPast = entryDate < today;
                    const hasIncomplete = entry.items?.some(item => !item.is_completed) || false;
                    let fillColor = '#ffffff'; // Default white for future
                    if (entry.count === 0) {
                      fillColor = '#1f2937'; // Dark gray/black for no tasks
                    } else if (isPast && hasIncomplete) {
                      fillColor = '#4b5563'; // Gray for overdue
                    } else if (isToday && !hasIncomplete) {
                      fillColor = '#ffffff'; // White for today if all completed
                    } else if (isToday) {
                      fillColor = '#9ca3af'; // Light gray for today if not all completed
                    } else if (!hasIncomplete) {
                      fillColor = '#ffffff'; // White if all completed
                    }
                    return <Cell key={`cell-${index}`} fill={fillColor} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <ClipboardList size={48} className="mx-auto mb-2 opacity-50" />
                <p>No tasks or subtasks with due dates yet</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Date Subtasks Modal */}
      {showDateSubtasksModal && selectedDateSubtasks.length > 0 && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowDateSubtasksModal(false)}
        >
          <div 
            className="bg-black rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-black border-b border-white/10 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-white">
                Tasks & Subtasks Due: {format(new Date(selectedDateSubtasks[0].due_date), 'MMMM dd, yyyy')}
              </h2>
              <button
                onClick={() => setShowDateSubtasksModal(false)}
                className="text-gray-400 hover:text-gray-200 transition-colors"
              >
                <AlertCircle size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-3 overflow-y-auto max-h-[calc(80vh-80px)]">
              {selectedDateSubtasks.map((item) => (
                <div
                  key={item.id}
                  onClick={() => {
                    setShowDateSubtasksModal(false);
                    handleOpenTask(item.problem_id);
                  }}
                  className="p-4 bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-white text-sm">{item.title}</h4>
                        {item.type === 'task' && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-500/20 text-indigo-400">
                            Task
                          </span>
                        )}
                        {item.type === 'subtask' && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-500/20 text-purple-400">
                            Subtask
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        {item.type === 'subtask' && item.problem?.title && (
                          <>
                            <span>Task: {item.problem.title}</span>
                          </>
                        )}
                        {item.assignee?.name && (
                          <>
                            {item.type === 'subtask' && item.problem?.title && <span>•</span>}
                            <span className="text-xs text-gray-400">
                              {(() => {
                                const isInactive = item.assignee.is_active === false;
                                return isInactive && item.assignee.inactive_user_name 
                                  ? item.assignee.inactive_user_name 
                                  : item.assignee.name;
                              })()}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                      item.is_completed ? 'bg-green-500/20 text-green-400' : 
                      (() => {
                        const dueDate = new Date(item.due_date);
                        dueDate.setHours(23, 59, 59, 999);
                        const today = new Date();
                        today.setHours(23, 59, 59, 999);
                        return dueDate < today;
                      })() ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {item.is_completed ? '✓ Completed' : 
                       (() => {
                         const dueDate = new Date(item.due_date);
                         dueDate.setHours(23, 59, 59, 999);
                         const today = new Date();
                         today.setHours(23, 59, 59, 999);
                         return dueDate < today;
                       })() ? '⚠ Overdue' : 
                       'Pending'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Experiments and Tasks Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Experiments List */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold mb-4 text-white flex items-center gap-2">
            <FlaskConical size={20} className="text-white" />
            Experiments ({experiments.length})
          </h3>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {experiments.length > 0 ? (
              experiments.map(exp => (
                <div
                  key={exp.id}
                  onClick={() => navigate(`/experiment-tracking/${projectId}`, { state: { openExperimentId: exp.id } })}
                  className="p-4 bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-white text-sm truncate">{exp.title}</h4>
                      <p className="text-xs text-gray-400 mt-1">{format(new Date(exp.created_at), 'MMM dd, yyyy')}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                      exp.status === 'Success' ? 'bg-white/10 text-white border border-white/20' :
                      exp.status === 'Failure' ? 'bg-white/5 text-gray-400 line-through opacity-50' :
                      exp.status === 'In Progress' ? 'bg-white/10 text-white font-bold' :
                      'bg-white/5 text-gray-500'
                    }`}>
                      {exp.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FlaskConical size={48} className="mx-auto mb-2 opacity-50" />
                <p>No experiments yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Subtasks List */}
        <div className="glass-card p-6">
          <h3 className="text-lg font-semibold mb-4 text-white flex items-center gap-2">
            <ClipboardList size={20} className="text-white" />
            Project Subtasks ({subtasks.length})
          </h3>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {subtasks.length > 0 ? (
              subtasks.map(subtask => (
                <div
                  key={subtask.id}
                  onClick={() => handleOpenTask(subtask.problem_id)}
                  className="p-4 bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-white text-sm truncate">{subtask.title}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        {subtask.problem?.title && (
                          <span className="text-xs text-gray-500">Task: {subtask.problem.title}</span>
                        )}
                        {subtask.assignee?.name && (
                          <>
                            <span className="text-gray-600">•</span>
                            <span className="text-xs text-gray-400">
                              {(() => {
                                const isInactive = subtask.assignee.is_active === false;
                                return isInactive && subtask.assignee.inactive_user_name 
                                  ? subtask.assignee.inactive_user_name 
                                  : subtask.assignee.name;
                              })()}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                      subtask.is_completed ? 'bg-white/10 text-white line-through opacity-50' : 'bg-white/10 text-white font-bold border border-white/20'
                    }`}>
                      {subtask.is_completed ? 'Completed' : 'In Progress'}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ClipboardList size={48} className="mx-auto mb-2 opacity-50" />
                <p>No subtasks yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Team Workload (if applicable) */}
      {teamWorkload.length > 0 && (
        <div className="glass-card p-6 mt-6">
          <h3 className="text-lg font-semibold mb-4 text-white flex items-center gap-2">
            <Users size={20} className="text-white" />
            Team Workload
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {teamWorkload.map(member => (
              <div key={member.name} className="p-4 bg-white/5 rounded-lg text-center">
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center mx-auto mb-2 shadow-lg shadow-white/10">
                  <span className="text-black font-bold">{member.name.charAt(0)}</span>
                </div>
                <div className="text-sm text-white font-medium truncate">{member.name}</div>
                <div className="text-xs text-gray-400 mt-1">{member.count} active tasks</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Task Detail Modal */}
      {showTaskModal && selectedTask && (
        <ProblemDetailModal
          problem={selectedTask}
          onClose={handleCloseTaskModal}
          onEdit={() => handleEditTask(selectedTask)}
          onUpdate={handleUpdateTask}
          onDelete={handleDeleteTask}
        />
      )}

      {/* Task Edit Form Modal */}
      {showEditFormModal && editingTask && (
        <ProblemFormModal
          problem={editingTask}
          onClose={() => {
            setShowEditFormModal(false);
            setEditingTask(null);
          }}
          onSubmit={handleUpdateTaskFull}
        />
      )}
    </div>
  );
};

export default ProjectDetail;
